/**
 * This is a crazy way to structure it. Definitely ripe for refactor.
 */
import { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SaveIcon from '@mui/icons-material/Save';
import { QuoteSubmissionProgress } from '../../common/QuoteSubmissionProgress/QuoteSubmissionProgress';

// connecting to the state for submitting the partially completed quote
import { connect, useSelector, useDispatch } from 'react-redux';
import { QuoteWizardState } from '../../../store';
import { QuoteWizardState as QuoteWizardStateType } from '../../../store/QuoteWizardState';
import { updateErrors } from '../../../store/QuoteWizardState';

// for the dialog
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';

// for the dialog form
import { Form } from 'informed';
import { EmailInput, TextField, TextArea } from '@calefy-inc/informedMaterial';

// in case of errors
import { IncompleteQuoteSubmissionError } from '../ErrorHandling';

// for adding in placeholder values for the unanswered questions
import { hybridAnswers } from '../../util/Quote';
import { answeredFormMatchesQuoteContext, isQuoteSubmissionInProgress } from '../utility';

// types and classes
import type { $TSFixMe, OrNullish } from '@calefy-inc/utilityTypes';
import { QuoteWizardAnswerInstance } from '../classes';
import { Dispatch, StoreState } from '../../../store';
import type { QuoteStatus } from '../../../Typescript/backend/types/QuoteStatus';
import { useWalnutGoogleAnalytics } from '../../../hooks';
import {
  EventActions,
  GAEventNames,
} from '../../../hooks/useWalnutGoogleAnalytics';
import { useQuoteSubmission } from '../../../hooks/useQuoteSubmission';
import { CoreTaskStatusChoices, GetTaskPoolQuery } from '../../../gql/graphql';
import { errorify } from '../../../util';
import { createApplicationSavedThunk } from '../../../store/analyticsStore';

interface SaveQuoteButtonProps {
  disabled: boolean;
  onSaveQuote: (
    clientEmail: OrNullish<string>,
    clientName: OrNullish<string>,
    message: OrNullish<string>,
  ) => void;
  submissionInProgress: boolean;
  okToSubmitIncompleteQuote: boolean;
  setOkToSubmitIncompleteQuote: (newValue: boolean) => void;
  contactEmail: string | undefined;
  contactName: string | undefined;
  quoteSubmitted: boolean;
}
/**
 * Button to save the current quote to the backend in a partially completed state in such a way that someone else can come along and complete it later
 * @param reconcileState - Function which should be called to reconcile the current state of the store with whatever information is currently entered into the tab on which this button is rendered. This should only deal with that portion of the state - appropriate actions to actually save the state will be taken elsewhere in the component
 */
const UnconnectedSaveQuoteButton = ({
  disabled = false,
  onSaveQuote,
  submissionInProgress,
  okToSubmitIncompleteQuote,
  setOkToSubmitIncompleteQuote,
  contactEmail,
  contactName,
  quoteSubmitted,
}: SaveQuoteButtonProps) => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [clientEmail, setClientEmail] = useState<string | undefined>(
    contactEmail,
  );
  const [clientName, setClientName] = useState<string | undefined>(contactName);
  const [message, setMessage] = useState<string | undefined>();

  // Get Save button state
  const visible = useSelector(
    (state: StoreState) => state.quoteWizard.saveButton.visible,
  );
  const reconcileState = useSelector(
    (state: StoreState) => state.quoteWizard.saveButton.reconcileState,
  );

  // if the state has been reconciled, then it is ok to submit the quote - but then immediately shut the door behind you!
  if (okToSubmitIncompleteQuote) {
    //console.log('About to submit quote from useeffect', {
    //   clientEmail,
    //   clientName,
    //   message,
    // });
    onSaveQuote(clientEmail, clientName, message);
    setOkToSubmitIncompleteQuote(false);
  }

  return visible ? (
    <>
      <Tooltip
        enterDelay={100}
        leaveDelay={100}
        title='Use this to save forms for later completion or sending to a trusted party'
      >
        <StyledSaveButton
          onClick={() => {
            reconcileState();
            setDialogOpen(true);
          }}
          disabled={disabled}
          variant='outlined'
          startIcon={<SaveIcon />}
        >
          Save For Later
        </StyledSaveButton>
      </Tooltip>
      <EmailDialog
        open={dialogOpen}
        setOpen={setDialogOpen}
        handleClose={() => {
          setDialogOpen(false);
        }}
        onOk={() => {
          reconcileState();
          setOkToSubmitIncompleteQuote(true);
        }}
        clientEmail={clientEmail}
        setClientEmail={setClientEmail}
        clientName={clientName}
        setClientName={setClientName}
        message={message}
        setMessage={setMessage}
        quoteSubmitted={quoteSubmitted}
        submissionInProgress={submissionInProgress}
      />
    </>
  ) : null;
};

export const SaveQuoteButton = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)((mappedState: SaveQuoteButtonProps) => (
  <UnconnectedSaveQuoteButton {...mappedState} />
));

interface EmailDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  handleClose: () => void;
  onOk: () => void;
  clientEmail: string | undefined;
  setClientEmail: (value: string | undefined) => void;
  clientName: string | undefined;
  setClientName: (value: string | undefined) => void;
  message: string | undefined;
  setMessage: (value: string | undefined) => void;
  quoteSubmitted: boolean;
  submissionInProgress: boolean;
}
/**
 * Dialog to ask the user for an email to send the partially completed form to
 */
export const EmailDialog = ({
  open,
  setOpen,
  handleClose,
  onOk,
  clientEmail,
  setClientEmail,
  clientName,
  setClientName,
  message,
  setMessage,
  quoteSubmitted,
  submissionInProgress,
}: EmailDialogProps) => {
  // whether the email is valid -> we can use the OK button
  const [pending, setPending] = useState<boolean>(false);
  const formApiRef = useRef<$TSFixMe>();
  const dispatch = useDispatch();
  const walnutGoogleAnalytics = useWalnutGoogleAnalytics();
  const { taskPool, quote } = useQuoteSubmission({
    active: open,
    onComplete: () => {
      dispatch(createApplicationSavedThunk());
    },
  });

  const { goToStep, componentMapping, currentStep } = useSelector(
    (state: StoreState) => state.quoteWizard.stepWizard,
  );

  useEffect(() => {
    if (quoteSubmitted) {
      setOpen(false);
      setPending(false);
      sendSaveWalnutGoogleAnalytics({
        googleAnalytics: walnutGoogleAnalytics,
        componentMapping,
        currentStep,
      });
      if (goToStep && componentMapping['done']) {
        /*console.log('GoToStep in savequotebutton'); */
        goToStep(componentMapping['done']);
      } else {
        dispatch(
          updateErrors({
            incompleteQuoteSubmission: 'No Done Component found',
          }),
        );
      }
    }
  }, [quoteSubmitted, setOpen, componentMapping, dispatch, goToStep]);

  useEffect(() => {
    if (!submissionInProgress) {
      setPending(false);
    }
  }, [submissionInProgress]);

  // Update errors in state when there are errors from quote submission
  useEffect(() => {
    const taskPoolData = taskPool.data?.getTaskPool;
    const error = taskPool.error || taskPoolData?.error || quote.error;
    // onsole.log({ error });

    if (error) {
      //console.log('About to dispatch error');
      dispatch(
        updateErrors({
          incompleteQuoteSubmission: errorify(error),
        }),
      );
    }
  }, [
    taskPool.error,
    taskPool.data?.getTaskPool?.error,
    quote.error,
    dispatch,
  ]);

  // Calculate progress percentage and check if task pool is complete
  const taskPoolData = taskPool.data?.getTaskPool;
  const hasTaskPoolErrors = !!taskPool.error || !!taskPoolData?.error;
  const hasQuoteErrors = !!quote.error;

  const progressPercentage = taskPoolData
    ? getProgressPercentage(taskPoolData)
    : 0;
  const isTaskPoolComplete = progressPercentage === 100;

  // Determine loading state: loading if queries are loading OR task pool is not complete and no errors
  const isLoading =
    taskPool.loading ||
    quote.loading ||
    (taskPoolData &&
      !isTaskPoolComplete &&
      !hasTaskPoolErrors &&
      !hasQuoteErrors);
  const shouldShowProgress =
    taskPoolData && !hasTaskPoolErrors && !hasQuoteErrors;

  return (
    <>
      <EmailSaveDialog
        open={open}
        onClose={handleClose}
        aria-labelledby='form-dialog-title'
      >
        <EmailDialogContentContainer>
          <EmailDialogTitle variant='h6' id='form-dialog-title' color='primary'>
            Save and Send Application
          </EmailDialogTitle>
          <DialogContent>
            <EmailDialogInstructionText variant='body2'>
              Please enter your email below if you wish to save and complete
              your application at a later time.
            </EmailDialogInstructionText>
            <EmailDialogForm
              initialValues={{ clientName, email: clientEmail, message }}
              formApiRef={formApiRef}
              onSubmit={({ values }) => {
                const clientName = values.clientName
                  ? String(values.clientName)
                  : undefined;
                const clientEmail = values.email
                  ? String(values.email)
                  : undefined;
                const message = values.message
                  ? String(values.message)
                  : undefined;
                setClientName(clientName);
                setClientEmail(clientEmail);
                setMessage(message);
                setPending(true);
                onOk();
              }}
            >
              {() => {
                return (
                  <>
                    <EmailDialogTextField
                      name='clientName'
                      Tabel='(Optional) Enter recipient name'
                      fullWidth
                      variant='standard'
                    />
                    <EmailDialogEmailInput
                      name='email'
                      label='Enter email'
                      required={true}
                      fullWidth
                      validateOnChange={true}
                    />
                    <EmailDialogTextArea
                      name='message'
                      label='(Optional) Enter a short message for the recipient'
                      fullWidth
                    />
                    <QuoteSubmissionProgress
                      progressPercentage={progressPercentage}
                      show={!!shouldShowProgress}
                    />
                    <EmailDialogButtonGroup>
                      <Button onClick={handleClose}>Cancel</Button>
                      <Button
                        disabled={Boolean(
                          !formApiRef.current || pending || isLoading,
                        )}
                        type='submit'
                        variant='contained'
                      >
                        {!pending && !isLoading
                          ? 'OK'
                          : isLoading
                          ? 'Processing...'
                          : 'Saving...'}
                      </Button>
                    </EmailDialogButtonGroup>
                  </>
                );
              }}
            </EmailDialogForm>
          </DialogContent>
        </EmailDialogContentContainer>
        <IncompleteQuoteSubmissionError />
      </EmailSaveDialog>
    </>
  );
};

/* Connect Layer */

function mapStateToProps(state: StoreState) {
  const answersByFormId = state.quoteWizard.formAnswers;
  const selectedBusinessType = state.quoteWizard.selectedBusinessType;

  const allSelectedPolicyIds = state.quoteWizard.selectedPolicies.map(
    (fullPolicy) => fullPolicy.id,
  );
  const formsWithAnswers = Object.values(answersByFormId).filter(
    (answeredForm) =>
      answeredFormMatchesQuoteContext(
        answeredForm,
        selectedBusinessType,
        allSelectedPolicyIds,
      ),
  );

  const submissionInProgress = isQuoteSubmissionInProgress(
    formsWithAnswers,
    state.quoteWizard.quoteSubmissionTaskPoolId,
  );

  const submittedQuoteForBusinessType = selectedBusinessType
    ? state.quoteWizard.submittedQuotes.find(
        // @ts-expect-error
        (quote) => quote.businessLine.id === selectedBusinessType.id,
      )
    : undefined;

  // @ts-expect-error
  const businessInformationFormId = state.quoteWizard.businessForm.id;
  const businessFormAnswers = state.quoteWizard.formAnswers[
    businessInformationFormId
  ]
    ? state.quoteWizard.formAnswers[businessInformationFormId].answers
    : undefined;
  // @ts-expect-error
  const contactEmail = findEmailInAnswerList(businessFormAnswers);
  // @ts-expect-error
  const contactName = findPrimaryContactNameInAnswerList(businessFormAnswers);

  const allRelevantForms = [
    state.quoteWizard.businessForm,
    ...state.quoteWizard.policyForms,
  ].filter(
    // @ts-expect-error
    (form) => !form.policy || allSelectedPolicyIds.includes(form.policy.id),
  );
  //console.log({ quotes: state.quoteWizard.submittedQuotes });
  const quoteSubmitted =
    state.quoteWizard.submittedQuotes.length > 0 &&
    !!state.quoteWizard.submittedQuotes.find((quote) =>
      ['INCOMPLETE', 'INCOMPLETE_RENEWAL', 'INCOMPLETE_CONFIRMATION'].includes(
        quote.status,
      ),
    );

  /*console.log( */
  /*   `in mapDispatchToProps with currentQuoteUUID=${state.quoteWizard.currentQuoteUUID}`, */
  /* ); */
  return {
    formsWithAnswers,
    allRelevantForms,
    selectedBusinessType,
    submissionInProgress,
    submittedQuoteForBusinessType,
    okToSubmitIncompleteQuote: state.quoteWizard.okToSubmitIncompleteQuote,
    currentQuoteUUID: state.quoteWizard.currentQuoteUUID,
    contactEmail,
    contactName,
    quoteSubmitted,
    renewalInProgress: state.quoteWizard.renewalInProgress,
    confirmationInProgress: state.quoteWizard.confirmationInProgress,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  const { confirmAnswersForForms, setOkToSubmitIncompleteQuote } =
    QuoteWizardState.actions;

  interface OnSaveQuoteArgument {
    formsWithAnswers: $TSFixMe;
    businessType: $TSFixMe;
    clientEmail: string;
    clientName: $TSFixMe;
    message: $TSFixMe;
    status: QuoteStatus;
    currentQuoteUUID: StoreState['quoteWizard']['currentQuoteUUID'];
    renewalInProgress: StoreState['quoteWizard']['renewalInProgress'];
  }
  function onSaveQuote({
    formsWithAnswers,
    businessType,
    clientEmail,
    clientName,
    message,
    status,
    currentQuoteUUID,
    renewalInProgress,
  }: OnSaveQuoteArgument) {
    return dispatch(
      // @ts-expect-error
      confirmAnswersForForms({
        businessType,
        formsWithAnswers,
        clientEmail,
        clientName,
        message,
        status,
        currentQuoteUUID,
        renewalInProgress,
      }),
    );
  }

  return {
    onSaveQuote,
    setOkToSubmitIncompleteQuote: (newValue: boolean) => {
      dispatch(
        setOkToSubmitIncompleteQuote({ okToSubmitIncompleteQuote: newValue }),
      );
    },
  };
}

function mergeProps(
  mappedState: ReturnType<typeof mapStateToProps>,
  mappedDispatch: ReturnType<typeof mapDispatchToProps>,
  ownProps: $TSFixMe,
) {
  const onSaveQuote = (
    clientEmail: OrNullish<string>,
    clientName: OrNullish<string>,
    message: OrNullish<string>,
  ) => {
    return mappedDispatch.onSaveQuote({
      businessType: mappedState.selectedBusinessType,
      formsWithAnswers: hybridAnswers(
        mappedState.formsWithAnswers,
        // @ts-expect-error
        mappedState.allRelevantForms,
      ),
      clientEmail,
      clientName,
      message,
      status: mappedState.renewalInProgress
        ? 'INCOMPLETE_RENEWAL'
        : mappedState.confirmationInProgress
        ? 'INCOMPLETE_CONFIRMATION'
        : 'INCOMPLETE',
      currentQuoteUUID: mappedState.currentQuoteUUID,
      renewalInProgress: mappedState.renewalInProgress,
    });
  };

  return {
    ...mappedState,
    ...mappedDispatch,
    ...ownProps,
    onSaveQuote,
  };
}

// ----------------------------------------------------------------------
// Utility Functions
// ----------------------------------------------------------------------

function findEmailInAnswerList(
  answerList: Array<QuoteWizardAnswerInstance>,
): QuoteWizardAnswerInstance | undefined {
  if (!answerList || answerList.length === 0) {
    return undefined;
  }
  for (let answer of answerList) {
    if (!answer) {
      continue;
    }
    /*console.log('About to match name for', answer, 'to email'); */
    /*console.log('findEmailInAnswerList question:', answer.questionInstance); */
    if (answer.questionInstance.apiName.match(/email/i)) {
      return answer.value;
    } else {
      const foundAnswerInSubAnswer = findEmailInAnswerList(answer.subAnswers);
      if (foundAnswerInSubAnswer) {
        return foundAnswerInSubAnswer;
      }
    }
  }
  return undefined;
}

function findPrimaryContactNameInAnswerList(
  answerList: Array<QuoteWizardAnswerInstance>,
): QuoteWizardAnswerInstance | undefined {
  if (!answerList || answerList.length === 0) {
    return;
  }
  for (let answer of answerList) {
    if (!answer) {
      continue;
    }
    if (answer.questionInstance.apiName.match(/client contact name/i)) {
      return answer.value;
    } else {
      const foundAnswerInSubAnswer = findPrimaryContactNameInAnswerList(
        answer.subAnswers,
      );
      if (foundAnswerInSubAnswer) {
        return foundAnswerInSubAnswer;
      }
    }
  }
  return;
}

function sendSaveWalnutGoogleAnalytics({
  googleAnalytics,
  componentMapping,
  currentStep,
}: {
  googleAnalytics: ReturnType<typeof useWalnutGoogleAnalytics>;
  componentMapping: QuoteWizardStateType['stepWizard']['componentMapping'];
  currentStep: QuoteWizardStateType['stepWizard']['currentStep'];
}) {
  if (!googleAnalytics || !componentMapping || !currentStep) {
    return;
  }
  const { stepName } = Object.entries(componentMapping).reduce(
    (acc, [key, step]) => {
      const { stepNumber } = acc;
      if (step <= currentStep && step > stepNumber) {
        return {
          stepNumber: step,
          stepName: key,
        };
      }
      return acc;
    },
    { stepNumber: 0, stepName: '' },
  );
  let eventName: GAEventNames;
  switch (stepName) {
    case 'business':
      eventName = GAEventNames.Business;
      break;
    case 'policy':
      eventName = GAEventNames.Policy;
      break;
    case 'General Information':
      eventName = GAEventNames.GeneralInformation;
      break;
    case 'General Liability':
      eventName = GAEventNames.GeneralLiability;
      break;
    case 'Property':
      eventName = GAEventNames.Property;
      break;
    case 'review':
      eventName = GAEventNames.Review;
      break;
    default:
      eventName = GAEventNames.Success;
  }
  googleAnalytics.triggerEvent(eventName, EventActions.Saved, stepName);
}

function getProgressPercentage(
  taskPool: NonNullable<GetTaskPoolQuery['getTaskPool']> | null | undefined,
): number {
  if (!taskPool?.tasks || taskPool.tasks.length === 0) {
    return 0;
  }

  const totalTasks = taskPool.tasks.length;
  // Treat FAILED as "finished" so optional task failures still advance progress.
  const finishedTasks = taskPool.tasks.filter(
    (task) =>
      task?.status === CoreTaskStatusChoices.Completed ||
      task?.status === CoreTaskStatusChoices.Failed,
  ).length;

  return (finishedTasks / totalTasks) * 100;
}

// ----------------------------------------------------------------------
// Styled Components
// ----------------------------------------------------------------------

const StyledSaveButton = styled(Button)({
  alignSelf: 'center',
});

const EmailSaveDialog = styled(Dialog)({
  display: 'flex',
  margin: 'auto',
  justifyContent: 'center',
  alignItems: 'center',
});

const EmailDialogContentContainer = styled('div')(({ theme }) => ({
  maxHeight: '90vh',
  maxWidth: '50rem',
  width: '100%',
  padding: `${theme.spacing(1.5)} 0`,
  position: 'relative',
  '& > h1': {
    marginTop: 0,
  },
}));

const EmailDialogTitle = styled(Typography)({
  textAlign: 'center',
});

const EmailDialogInstructionText = styled(Typography)({
  marginBottom: 0,
});

const EmailDialogForm = styled(Form)({
  display: 'flex',
  flexDirection: 'column',
});

const EmailDialogTextField = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: 0,
}));

const EmailDialogEmailInput = styled(EmailInput)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: 0,
}));

const EmailDialogTextArea = styled(TextArea)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: 0,
}));

const EmailDialogButtonGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(3),
}));
